import React from "react";
import WorldMapAnimation from "./WorldMapAnimation";
import { scrollToSection } from "@/lib/scroll";

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100svh",
        background: "#000",
        overflow: "hidden",
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Hero background image with slow zoom animation */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url(/hero-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          animation: "hero-bg-pan 30s ease-in-out infinite",
        }}
      />

      {/* Dark gradient overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.82) 100%)",
        }}
      />

      {/* Animated world map canvas with gold connections */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <WorldMapAnimation />
      </div>

      {/* Content - centered */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100svh",
          textAlign: "center",
          padding: "40px 30px",
        }}
      >
        {/* Semi-transparent overlay panel behind text */}
        <div
          style={{
            background: "rgba(0,0,0,0.30)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            borderRadius: "4px",
            padding: "48px 40px",
            maxWidth: "700px",
            border: "1px solid rgba(210,170,60,0.12)",
          }}
        >
          {/* Eyebrow with decorative lines */}
          <div
            style={{
              fontSize: "10px",
              letterSpacing: "0.42em",
              color: "rgba(210,170,60,0.8)",
              textTransform: "uppercase",
              marginBottom: "22px",
              animation: "hero-fadeUp 1s 0.4s both",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: "30px",
                height: "0.5px",
                background: "rgba(210,170,60,0.5)",
                verticalAlign: "middle",
                margin: "0 12px",
              }}
            />
            Cold Calling · Appointment Setting · Customer Support
            <span
              style={{
                display: "inline-block",
                width: "30px",
                height: "0.5px",
                background: "rgba(210,170,60,0.5)",
                verticalAlign: "middle",
                margin: "0 12px",
              }}
            />
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(42px, 7vw, 80px)",
              fontWeight: 300,
              lineHeight: 1.05,
              color: "#fff",
              letterSpacing: "0.03em",
              animation: "hero-fadeUp 1s 0.65s both",
              marginBottom: "8px",
            }}
          >
            Build a <em style={{ color: "#e8c45a", fontStyle: "italic", fontWeight: 400 }}>Stronger Team</em>
            <br />
            Without the Overhead.
          </h1>

          {/* Subtitle */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 200,
              letterSpacing: "0.22em",
              color: "rgba(230,200,120,0.65)",
              textTransform: "uppercase",
              marginBottom: "26px",
              animation: "hero-fadeUp 1s 0.85s both",
            }}
          >
            Trained · Managed · Accountable
          </div>

          {/* Description */}
          <p
            data-testid="hero-subheadline"
            style={{
              fontSize: "14px",
              fontWeight: 300,
              color: "rgba(220,195,140,0.72)",
              maxWidth: "460px",
              lineHeight: 1.95,
              letterSpacing: "0.03em",
              marginBottom: "42px",
              animation: "hero-fadeUp 1s 1.05s both",
              textAlign: "center",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            TMN Resource Solutions provides trained cold callers, appointment setters,
            and customer service support to help you generate leads, book meetings,
            and handle customer interactions efficiently.
          </p>

          {/* CTA Buttons */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              animation: "hero-fadeUp 1s 1.25s both",
              flexWrap: "wrap",
            }}
          >
            <button
              data-testid="hero-book-call-button"
              onClick={scrollToSection("#booking")}
              style={{
                padding: "13px 36px",
                fontSize: "11px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                fontFamily: "'Montserrat', sans-serif",
                cursor: "pointer",
                border: "1px solid rgba(210,165,40,0.8)",
                background: "rgba(210,165,40,0.18)",
                color: "#e8c45a",
                fontWeight: 400,
                transition: "all 0.35s",
                borderRadius: "0",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(210,165,40,0.32)";
                e.currentTarget.style.borderColor = "#e8c45a";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(210,165,40,0.18)";
                e.currentTarget.style.borderColor = "rgba(210,165,40,0.8)";
                e.currentTarget.style.color = "#e8c45a";
              }}
            >
              Book a Strategy Call
            </button>
            <button
              data-testid="hero-services-button"
              onClick={scrollToSection("#services")}
              style={{
                padding: "13px 36px",
                fontSize: "11px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                fontFamily: "'Montserrat', sans-serif",
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.22)",
                background: "transparent",
                color: "rgba(255,255,255,0.65)",
                fontWeight: 400,
                transition: "all 0.35s",
                borderRadius: "0",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.55)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)";
                e.currentTarget.style.color = "rgba(255,255,255,0.65)";
              }}
            >
              Explore Services
            </button>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div
        style={{
          position: "absolute",
          bottom: "58px",
          left: 0,
          right: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "center",
          gap: "clamp(24px, 5vw, 60px)",
          animation: "hero-fadeUp 1s 1.45s both",
          flexWrap: "wrap",
          padding: "0 20px",
        }}
      >
        {[
          { n: "50+", l: "Agents Deployed" },
          { n: "12+", l: "Industries Served" },
          { n: "24/7", l: "Live Monitoring" },
        ].map((s) => (
          <div key={s.l} style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "28px",
                fontWeight: 400,
                color: "#e8c45a",
              }}
            >
              {s.n}
            </div>
            <div
              style={{
                fontSize: "9px",
                letterSpacing: "0.25em",
                color: "rgba(200,160,50,0.5)",
                textTransform: "uppercase",
                marginTop: "4px",
              }}
            >
              {s.l}
            </div>
          </div>
        ))}
      </div>

      {/* Scroll hint */}
      <div
        style={{
          position: "absolute",
          bottom: "16px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          animation: "hero-fadeUp 1s 1.6s both",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "7px",
        }}
      >
        <span
          style={{
            fontSize: "9px",
            letterSpacing: "0.3em",
            color: "rgba(200,160,50,0.4)",
            textTransform: "uppercase",
          }}
        >
          Scroll
        </span>
        <div
          style={{
            width: "0.5px",
            height: "28px",
            background: "linear-gradient(to bottom, rgba(200,160,50,0.5), transparent)",
            animation: "hero-scrollPulse 2s infinite",
          }}
        />
      </div>
    </section>
  );
};

export default Hero;
